import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ShuttleBooking {
  id: string;
  booking_code: string;
  full_name: string;
  email: string;
  phone: string;
  pickup_location: string;
  pickup_type: string;
  dropoff_location: string;
  dropoff_type: string;
  route_type: string;
  vehicle_id: string | null;
  vehicle_name: string | null;
  service_type: string | null;
  passengers: number;
  distance_km: number | null;
  estimated_time_min: number | null;
  base_price: number;
  extras_cost: number;
  discount: number;
  total_price: number;
  trip_type: string;
  arrival_date: string | null;
  arrival_time: string | null;
  airline: string | null;
  flight_number: string | null;
  return_date: string | null;
  return_time: string | null;
  return_airline: string | null;
  return_flight_number: string | null;
  amount_of_luggage: string | null;
  remarks: string | null;
  extra_items: any;
  promo_code_id: string | null;
  promo_code: string | null;
  payment_method: string;
  payment_status: string;
  payment_id: string | null;
  booking_status: string;
  created_at: string;
  updated_at: string;
}

export const useShuttleBookings = () => {
  const [bookings, setBookings] = useState<ShuttleBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('shuttle_bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBookings((data || []) as ShuttleBooking[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching shuttle bookings:', err);
      setError('Error loading bookings');
    } finally {
      setLoading(false);
    }
  };

  const createBooking = async (bookingData: Omit<ShuttleBooking, 'id' | 'booking_code' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('shuttle_bookings')
        .insert({
          ...bookingData,
          booking_code: '', // Will be auto-generated by trigger
        })
        .select()
        .single();

      if (error) throw error;
      await fetchBookings();
      return data as ShuttleBooking;
    } catch (err: any) {
      console.error('Error creating shuttle booking:', err);
      throw new Error(`Error creating booking: ${err?.message || 'Unknown error'}`);
    }
  };

  const updateBookingStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('shuttle_bookings')
        .update({ booking_status: status })
        .eq('id', id);

      if (error) throw error;
      await fetchBookings();
    } catch (err) {
      console.error('Error updating booking status:', err);
      throw new Error('Error updating booking status');
    }
  };

  const deleteBooking = async (id: string) => {
    try {
      const { error } = await supabase
        .from('shuttle_bookings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchBookings();
    } catch (err) {
      console.error('Error deleting booking:', err);
      throw new Error('Error deleting booking');
    }
  };

  return {
    bookings,
    loading,
    error,
    createBooking,
    updateBookingStatus,
    deleteBooking,
    refetch: fetchBookings,
  };
};
